import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import MedStockLineChart, {
  type MedStockPoint
} from '../../ResidentEmarScreens/components/MedStockLineChart'
import {
  type ChartDataPoint,
  DiscrepancyStatus,
  type StockRecord
} from '../types'
import { getActionTitle } from '../utils/stockRecordUtils'

type MedStockChartProps = {
  medication: {
    stockQuantityType?: string
    name?: string
  }
  records: StockRecord[]
  chartData: ChartDataPoint[]
  startDate: dayjs.Dayjs
  endDate: dayjs.Dayjs
  testId?: string
}

/** Older stock records carry the action as `reason`. */
type ChartRecord = StockRecord & { reason?: StockRecord['action'] }

const ALERT_ICON = 'alert-icon'

/** The API stores RECONCILED; older records may still carry Resolved. */
const isResolved = (status?: DiscrepancyStatus) =>
  status === DiscrepancyStatus.RECONCILED ||
  status === DiscrepancyStatus.RESOLVED

const recordedByName = ({ recordedBy }: StockRecord) =>
  typeof recordedBy?.name === 'string'
    ? recordedBy.name
    : [recordedBy?.name?.first, recordedBy?.name?.last]
        .filter(Boolean)
        .join(' ')

/**
 * Stock levels from the backend's chartData, one point per stock record. Expected-stock
 * points and administrations are left out; the tooltip fields come from the full record.
 */
export const toMedStockPoints = (
  chartData: ChartDataPoint[],
  records: StockRecord[],
  t: (key: string) => string
): MedStockPoint[] => {
  const recordsById = new Map(records.map((record) => [record.id, record]))

  return chartData
    .flatMap((point) => {
      if (point.expectedStockLevel) {
        return []
      }
      const record: ChartRecord | undefined =
        (point.recordId && recordsById.get(point.recordId)) || point.record
      if (!record || record.type === 'administration') {
        return []
      }

      const action = record.action || record.reason
      const isStockCount =
        action === 'STOCK_COUNT' || point.reason === 'STOCK_COUNT'
      const hasUnresolvedDiscrepancy =
        (record.hasDiscrepancy || !!record.discrepancy) &&
        !isResolved(record.discrepancy?.status)
      const expected =
        typeof record.expectedStock === 'number'
          ? record.expectedStock
          : record.previousStockLevel
      const hasStockChanged =
        isStockCount &&
        typeof record.stockCount === 'number' &&
        typeof expected === 'number' &&
        record.stockCount !== expected &&
        hasUnresolvedDiscrepancy

      // The backend's marker, when it sends one, decides the icon; a null symbol is a plain dot.
      const marker: MedStockPoint['marker'] = point.marker
        ? point.marker.symbol
          ? point.marker.symbol.includes(ALERT_ICON)
            ? 'alert'
            : 'tick'
          : undefined
        : hasStockChanged
          ? 'alert'
          : 'tick'

      const counted =
        typeof record.stockCount === 'number'
          ? record.stockCount
          : action === 'STOCK_COUNT'
            ? record.resultingStockLevel
            : undefined
      const discrepancyReason =
        record.discrepancy?.reason || record.discrepancy?.carerReason

      return [
        {
          counted,
          discrepancy: discrepancyReason
            ? {
                reason: discrepancyReason,
                resolved: isResolved(record.discrepancy?.status)
              }
            : undefined,
          expected,
          marker,
          notes: Array.isArray(record.notes)
            ? record.notes.join(', ')
            : record.notes || '',
          recordedBy: recordedByName(record),
          stock: point.stockLevel,
          time: dayjs(point.date).valueOf(),
          title: getActionTitle(action, t, record.administrationStatus),
          sortTime: dayjs(record.date || record.actionTime).valueOf()
        }
      ]
    })
    .sort((a, b) => a.sortTime - b.sortTime)
    .map(({ sortTime: _sortTime, ...point }) => point)
}

const MedStockChart = ({
  medication,
  records,
  chartData,
  startDate,
  endDate,
  testId
}: MedStockChartProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { stockQuantityType } = medication

  return (
    <MedStockLineChart
      endDate={endDate}
      points={toMedStockPoints(chartData, records, t)}
      startDate={startDate}
      stockQuantityType={stockQuantityType}
      testId={testId}
    />
  )
}

export default MedStockChart
